import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import wooStoreApiService from "@/api/services/wooStoreApiService";
import { handleApiError } from "@/helpers/apiErrorHelper";
import useAppDispatch from "@/hooks/useAppDispatch";
import { DEFAULT_MAX_QUANTITY } from "@/constants/common";
import type { Cart } from "@/types/cart";

export interface UseCartReturn {
  cart: Cart | null;
  isCartInitialized: boolean;
  isCartEmpty: boolean;
  cartUpdatingStatus: Record<string, boolean>;
  isCartUpdating: boolean;
  quantityInputs: Record<string, number>;
  handleQuantityChange: (itemKey: string, newQuantity: number) => void;
  handleRemoveCartItem: (itemKey: string) => void;
  clearCart: () => Promise<void>;
  canCheckoutCart: () => Promise<boolean>;
}

const useCart = (): UseCartReturn => {
  const cart = useSelector((store: RootState) => store.cart);
  const appDispatch = useAppDispatch();
  const isInitialized = useRef(false);

  const [quantityInputs, setQuantityInputs] = useState<Record<string, number>>({}); // { key1: quantity }
  const [cartUpdatingStatus, setCartUpdatingStatus] = useState<Record<string, boolean>>({});

  const isCartEmpty = useMemo(() => !cart?.items.length, [cart]);

  const isUpdating = useMemo(
    () => Object.values(cartUpdatingStatus).some((value) => value),
    [cartUpdatingStatus],
  );

  const fetchCart = async (signal?: AbortSignal): Promise<Cart | undefined> => {
    setCartUpdatingStatus((prev) => ({ ...prev, global: true }));

    try {
      const { data } = await wooStoreApiService.cart.getCartData(signal);
      appDispatch.setFullCart(data);
      return data;
    } catch (error) {
      handleApiError(error, appDispatch, "無法取得購物車資料，請稍後再試");
    } finally {
      if (signal?.aborted) return;
      if (!isInitialized.current) {
        isInitialized.current = true;
      }
      setCartUpdatingStatus((prev) => ({ ...prev, global: false }));
    }
  };

  const updateCartItemQuantity = async (
    itemKey: string,
    newQuantity: number,
    backupQuantityInputs: Record<string, number>,
  ) => {
    setCartUpdatingStatus((prev) => ({ ...prev, [itemKey]: true }));

    try {
      const { data } = await wooStoreApiService.cart.updateCartItem({
        key: itemKey,
        quantity: newQuantity,
      });

      appDispatch.setFullCart(data);
    } catch (error) {
      setQuantityInputs(backupQuantityInputs); // optimistic rollback
      handleApiError(error, appDispatch, "數量修改失敗，請重新再試一次");
    } finally {
      setCartUpdatingStatus((prev) => ({ ...prev, [itemKey]: false }));
    }
  };

  const handleQuantityChange = async (itemKey: string, newQuantity: number) => {
    const backupQuantityInputs = { ...quantityInputs };
    setQuantityInputs((prev) => ({ ...prev, [itemKey]: newQuantity })); // optimistic update

    const cartItem = cart?.items.find((item) => item.key === itemKey);
    const maxQuantity = cartItem?.quantity_limits.maximum ?? DEFAULT_MAX_QUANTITY;
    if (newQuantity > maxQuantity) {
      appDispatch.pushAlert({
        type: "warning",
        title: `${cartItem?.name} 僅剩餘 ${maxQuantity} 件`,
      });
      setQuantityInputs(backupQuantityInputs);
      return;
    }

    updateCartItemQuantity(itemKey, newQuantity, backupQuantityInputs);
  };

  const handleRemoveCartItem = async (itemKey: string) => {
    setCartUpdatingStatus((prev) => ({ ...prev, [itemKey]: true }));
    const itemName = cart?.items.find((item) => item.key === itemKey)?.name;

    // optimistic update
    const backupQuantityInputs = { ...quantityInputs };
    setQuantityInputs((prev) => ({ ...prev, [itemKey]: 0 }));

    try {
      await wooStoreApiService.cart.removeCartItem(itemKey);
      appDispatch.pushAlert({
        type: "success",
        title: `「${itemName}」已從購物車移除`,
      });

      await fetchCart(); // 因 remove api 無回應資料(與官方文件有異)，故重取購物車來更新 store
    } catch (error) {
      appDispatch.pushAlert({
        type: "warning",
        title: `「${itemName}」移除失敗，請重新操作一次`,
      });

      // optimistic rollback
      setQuantityInputs(backupQuantityInputs);
    } finally {
      setCartUpdatingStatus((prev) => ({ ...prev, [itemKey]: false }));
    }
  };

  const clearCart = async () => {
    setCartUpdatingStatus((prev) => ({ ...prev, global: true }));

    try {
      await wooStoreApiService.cartItem.deleteAllCartItems();
      appDispatch.clearCartItems();
      setQuantityInputs({});
    } catch (error) {
      handleApiError(error, appDispatch, "清空購物車失敗，請稍後再試");
    } finally {
      setCartUpdatingStatus((prev) => ({ ...prev, global: false }));
    }
  };

  const canCheckoutCart = async (): Promise<boolean> => {
    try {
      const cartData = await fetchCart();
      if ((cartData?.errors ?? []).length > 0) {
        // 因為 fetchCart 會更新 store cart，錯誤會在那時重新渲染顯示，這邊不做處理
        return false;
      } else {
        return true;
      }
    } catch (error) {
      return false;
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchCart(controller.signal);

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (!cart) return;

    const transformedItemQuantities = Object.fromEntries(
      cart.items.map((item) => [item.key, item.quantity]),
    );

    setQuantityInputs((prev) => {
      // 避免不必要的狀態更新，提升效能
      if (JSON.stringify(prev) !== JSON.stringify(transformedItemQuantities)) {
        return transformedItemQuantities;
      }
      return prev;
    });
  }, [cart?.items]);

  return {
    cart,
    isCartInitialized: isInitialized.current,
    isCartEmpty,
    cartUpdatingStatus,
    isCartUpdating: isUpdating,
    quantityInputs,
    handleQuantityChange,
    handleRemoveCartItem,
    clearCart,
    canCheckoutCart,
  };
};

export default useCart;
